/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListOrganizationUserSessionIssuersRequest,
  ListOrganizationUserSessionIssuersSecurity,
  QueryParamTier,
} from "../models/operations/listorganizationusersessionissuers.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationUserSessionIssuersInfiniteQuery,
  buildOrganizationUserSessionIssuersQuery,
  OrganizationUserSessionIssuersInfiniteQueryData,
  OrganizationUserSessionIssuersPageParams,
  OrganizationUserSessionIssuersQueryData,
  prefetchOrganizationUserSessionIssuers,
  prefetchOrganizationUserSessionIssuersInfinite,
  queryKeyOrganizationUserSessionIssuers,
  queryKeyOrganizationUserSessionIssuersInfinite,
} from "./organizationUserSessionIssuers.core.js";
export {
  buildOrganizationUserSessionIssuersInfiniteQuery,
  buildOrganizationUserSessionIssuersQuery,
  type OrganizationUserSessionIssuersInfiniteQueryData,
  type OrganizationUserSessionIssuersPageParams,
  type OrganizationUserSessionIssuersQueryData,
  prefetchOrganizationUserSessionIssuers,
  prefetchOrganizationUserSessionIssuersInfinite,
  queryKeyOrganizationUserSessionIssuers,
  queryKeyOrganizationUserSessionIssuersInfinite,
};

export type OrganizationUserSessionIssuersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listIssuers organizationUserSessionIssuers
 *
 * @remarks
 * List the user_session_issuers in the caller's organization: organization-owned and project-specific, each project-specific issuer with its owning project name. Requires org:read.
 */
export function useOrganizationUserSessionIssuers(
  request?: ListOrganizationUserSessionIssuersRequest | undefined,
  security?: ListOrganizationUserSessionIssuersSecurity | undefined,
  options?: QueryHookOptions<
    OrganizationUserSessionIssuersQueryData,
    OrganizationUserSessionIssuersQueryError
  >,
): UseQueryResult<
  OrganizationUserSessionIssuersQueryData,
  OrganizationUserSessionIssuersQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationUserSessionIssuersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listIssuers organizationUserSessionIssuers
 *
 * @remarks
 * List the user_session_issuers in the caller's organization: organization-owned and project-specific, each project-specific issuer with its owning project name. Requires org:read.
 */
export function useOrganizationUserSessionIssuersSuspense(
  request?: ListOrganizationUserSessionIssuersRequest | undefined,
  security?: ListOrganizationUserSessionIssuersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationUserSessionIssuersQueryData,
    OrganizationUserSessionIssuersQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationUserSessionIssuersQueryData,
  OrganizationUserSessionIssuersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationUserSessionIssuersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listIssuers organizationUserSessionIssuers
 *
 * @remarks
 * List the user_session_issuers in the caller's organization: organization-owned and project-specific, each project-specific issuer with its owning project name. Requires org:read.
 */
export function useOrganizationUserSessionIssuersInfinite(
  request?: ListOrganizationUserSessionIssuersRequest | undefined,
  security?: ListOrganizationUserSessionIssuersSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    OrganizationUserSessionIssuersInfiniteQueryData,
    OrganizationUserSessionIssuersQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    OrganizationUserSessionIssuersInfiniteQueryData,
    OrganizationUserSessionIssuersPageParams
  >,
  OrganizationUserSessionIssuersQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    OrganizationUserSessionIssuersInfiniteQueryData,
    OrganizationUserSessionIssuersQueryError,
    InfiniteData<
      OrganizationUserSessionIssuersInfiniteQueryData,
      OrganizationUserSessionIssuersPageParams
    >,
    QueryKey,
    OrganizationUserSessionIssuersPageParams
  >({
    ...buildOrganizationUserSessionIssuersInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listIssuers organizationUserSessionIssuers
 *
 * @remarks
 * List the user_session_issuers in the caller's organization: organization-owned and project-specific, each project-specific issuer with its owning project name. Requires org:read.
 */
export function useOrganizationUserSessionIssuersInfiniteSuspense(
  request?: ListOrganizationUserSessionIssuersRequest | undefined,
  security?: ListOrganizationUserSessionIssuersSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    OrganizationUserSessionIssuersInfiniteQueryData,
    OrganizationUserSessionIssuersQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    OrganizationUserSessionIssuersInfiniteQueryData,
    OrganizationUserSessionIssuersPageParams
  >,
  OrganizationUserSessionIssuersQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    OrganizationUserSessionIssuersInfiniteQueryData,
    OrganizationUserSessionIssuersQueryError,
    InfiniteData<
      OrganizationUserSessionIssuersInfiniteQueryData,
      OrganizationUserSessionIssuersPageParams
    >,
    QueryKey,
    OrganizationUserSessionIssuersPageParams
  >({
    ...buildOrganizationUserSessionIssuersInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setOrganizationUserSessionIssuersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      tier?: QueryParamTier | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: OrganizationUserSessionIssuersQueryData,
): OrganizationUserSessionIssuersQueryData | undefined {
  const key = queryKeyOrganizationUserSessionIssuers(...queryKeyBase);

  return client.setQueryData<OrganizationUserSessionIssuersQueryData>(
    key,
    data,
  );
}

export function invalidateOrganizationUserSessionIssuers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      tier?: QueryParamTier | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationUserSessionIssuers",
      "list",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationUserSessionIssuers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "organizationUserSessionIssuers", "list"],
  });
}
